import { useState } from "react";
import type { Project } from "@cowork/shared";
import { cn } from "../../lib/utils.js";
import { useProjectStore, PROJECT_COLORS } from "../../store/projectStore.js";

type DetailTab = "overview" | "memory";

// ── Project dot avatar ─────────────────────────────────────────────────────────
function ProjectDot({ color, size = 8 }: { color: string; size?: number }) {
  return (
    <span
      className="rounded-full flex-shrink-0 inline-block"
      style={{ width: size, height: size, background: color }}
    />
  );
}

// ── Empty state ────────────────────────────────────────────────────────────────
function EmptyProjects({ onCreate }: { onCreate: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center h-full text-center px-8">
      <svg width="36" height="36" viewBox="0 0 36 36" fill="none" className="mb-3" style={{ color: "var(--text-tertiary)" }}>
        <rect x="4" y="8" width="28" height="22" rx="3" stroke="currentColor" strokeWidth="1.5" fill="none" />
        <path d="M4 13h28" stroke="currentColor" strokeWidth="1.5" />
        <path d="M14 8V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
      <h3 className="text-sm font-medium mb-1" style={{ color: "var(--text-primary)" }}>No projects yet</h3>
      <p className="text-xs mb-4" style={{ color: "var(--text-secondary)" }}>
        Projects keep your conversations, instructions, and memory organized.
      </p>
      <button
        onClick={onCreate}
        className="px-4 py-2 text-xs font-medium rounded-lg transition-colors"
        style={{ background: "var(--accent)", color: "white" }}
      >
        Create your first project
      </button>
    </div>
  );
}

// ── Memory item row ────────────────────────────────────────────────────────────
function MemoryRow({
  content,
  onEdit,
  onDelete,
}: {
  content: string;
  onEdit: (next: string) => void;
  onDelete: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(content);

  const commit = () => {
    if (draft.trim()) onEdit(draft.trim());
    setEditing(false);
  };

  return (
    <div
      className="group flex items-start gap-2 px-3 py-2 rounded-lg"
      style={{ background: "var(--bg-tertiary)" }}
    >
      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" className="mt-0.5 flex-shrink-0" style={{ color: "var(--accent)" }}>
        <circle cx="6" cy="6" r="2" fill="currentColor" />
        <circle cx="6" cy="6" r="5" stroke="currentColor" strokeWidth="1" fill="none" />
      </svg>
      {editing ? (
        <div className="flex-1 flex flex-col gap-1">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); commit(); } if (e.key === "Escape") setEditing(false); }}
            className="w-full text-xs resize-none rounded-md px-2 py-1 outline-none"
            style={{ background: "var(--bg-secondary)", border: "1px solid var(--accent-border)", color: "var(--text-primary)", minHeight: 56 }}
            autoFocus
          />
          <div className="flex gap-1.5">
            <button onClick={commit} className="text-[10px] px-2 py-0.5 rounded" style={{ background: "var(--accent)", color: "white" }}>Save</button>
            <button onClick={() => setEditing(false)} className="text-[10px] px-2 py-0.5 rounded" style={{ color: "var(--text-secondary)" }}>Cancel</button>
          </div>
        </div>
      ) : (
        <>
          <p className="flex-1 text-xs leading-relaxed" style={{ color: "var(--text-primary)" }}>{content}</p>
          <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
            <button onClick={() => { setDraft(content); setEditing(true); }} title="Edit" className="w-5 h-5 flex items-center justify-center rounded" style={{ color: "var(--text-tertiary)" }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = "var(--text-primary)"; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = "var(--text-tertiary)"; }}>
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M7 1l2 2-6 6H1V7l6-6z" /></svg>
            </button>
            <button onClick={onDelete} title="Delete" className="w-5 h-5 flex items-center justify-center rounded" style={{ color: "var(--text-tertiary)" }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = "#f87171"; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = "var(--text-tertiary)"; }}>
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.5"><line x1="1" y1="1" x2="9" y2="9" /><line x1="9" y1="1" x2="1" y2="9" /></svg>
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ── Project detail ─────────────────────────────────────────────────────────────
function ProjectDetail({
  project,
  onDelete,
}: {
  project: Project;
  onDelete: () => void;
}) {
  const { updateProject, addMemory, updateMemory, removeMemory } = useProjectStore();
  const [tab, setTab] = useState<DetailTab>("overview");
  const [newMemory, setNewMemory] = useState("");

  const handleAddMemory = () => {
    if (!newMemory.trim()) return;
    addMemory(project.id, newMemory.trim());
    setNewMemory("");
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 flex-shrink-0" style={{ borderBottom: "1px solid var(--border)" }}>
        <div className="flex items-center gap-3 min-w-0">
          <ProjectDot color={project.color} size={10} />
          <h2 className="text-sm font-semibold truncate" style={{ color: "var(--text-primary)" }}>
            {project.title}
          </h2>
        </div>
        <button
          onClick={onDelete}
          className="text-xs px-3 py-1.5 rounded-lg transition-colors flex-shrink-0"
          style={{ color: "#f87171", border: "1px solid rgba(248,113,113,0.2)" }}
          onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = "rgba(248,113,113,0.08)"; }}
          onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = "transparent"; }}
        >
          Delete project
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 px-6 pt-3 flex-shrink-0" style={{ borderBottom: "1px solid var(--border)" }}>
        {(["overview", "memory"] as DetailTab[]).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={cn(
              "px-3 py-1.5 text-xs capitalize transition-colors border-b-2 -mb-px",
              tab === t
                ? "border-[var(--accent)] text-[var(--accent)]"
                : "border-transparent text-[var(--text-secondary)] hover:text-[var(--text-primary)]",
            )}
          >
            {t === "memory" ? `Memory (${project.memory.length})` : "Overview"}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto p-6">
        {tab === "overview" && (
          <div className="space-y-5 max-w-xl">
            {/* Title */}
            <div>
              <label className="text-[10px] uppercase tracking-wider font-medium block mb-1.5" style={{ color: "var(--text-secondary)" }}>Title</label>
              <input
                type="text"
                defaultValue={project.title}
                onBlur={(e) => updateProject(project.id, { title: e.target.value.trim() || project.title })}
                className="w-full text-sm px-3 py-2 rounded-lg outline-none"
                style={{ background: "var(--bg-tertiary)", border: "1px solid var(--border)", color: "var(--text-primary)" }}
                onFocus={(e) => { (e.currentTarget as HTMLElement).style.borderColor = "var(--accent-border)"; }}
              />
            </div>

            {/* Description */}
            <div>
              <label className="text-[10px] uppercase tracking-wider font-medium block mb-1.5" style={{ color: "var(--text-secondary)" }}>Description</label>
              <textarea
                defaultValue={project.description}
                onBlur={(e) => updateProject(project.id, { description: e.target.value })}
                placeholder="What is this project about?"
                rows={2}
                className="w-full text-sm px-3 py-2 rounded-lg outline-none resize-none"
                style={{ background: "var(--bg-tertiary)", border: "1px solid var(--border)", color: "var(--text-primary)" }}
                onFocus={(e) => { (e.currentTarget as HTMLElement).style.borderColor = "var(--accent-border)"; }}
              />
            </div>

            {/* Instructions */}
            <div>
              <label className="text-[10px] uppercase tracking-wider font-medium block mb-1" style={{ color: "var(--text-secondary)" }}>Custom Instructions</label>
              <p className="text-[11px] mb-2" style={{ color: "var(--text-tertiary)" }}>
                Prepended as system prompt for every conversation in this project.
              </p>
              <textarea
                defaultValue={project.instructions}
                onBlur={(e) => updateProject(project.id, { instructions: e.target.value })}
                placeholder="You are a legal review assistant specializing in commercial real estate contracts..."
                rows={5}
                className="w-full text-xs font-mono px-3 py-2.5 rounded-lg outline-none resize-none"
                style={{ background: "var(--bg-tertiary)", border: "1px solid var(--border)", color: "var(--text-primary)" }}
                onFocus={(e) => { (e.currentTarget as HTMLElement).style.borderColor = "var(--accent-border)"; }}
              />
            </div>

            {/* Color */}
            <div>
              <label className="text-[10px] uppercase tracking-wider font-medium block mb-2" style={{ color: "var(--text-secondary)" }}>Color</label>
              <div className="flex gap-2">
                {PROJECT_COLORS.map((c) => (
                  <button
                    key={c}
                    onClick={() => updateProject(project.id, { color: c })}
                    className="w-6 h-6 rounded-full transition-transform"
                    style={{
                      background: c,
                      outline: project.color === c ? `2px solid ${c}` : "none",
                      outlineOffset: 2,
                      transform: project.color === c ? "scale(1.15)" : "scale(1)",
                    }}
                  />
                ))}
              </div>
            </div>
          </div>
        )}

        {tab === "memory" && (
          <div className="space-y-3 max-w-xl">
            <p className="text-xs" style={{ color: "var(--text-secondary)" }}>
              Facts Claude should always remember for this project. These are injected into every conversation.
            </p>

            {/* Add new */}
            <div className="flex gap-2">
              <input
                type="text"
                value={newMemory}
                onChange={(e) => setNewMemory(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") handleAddMemory(); }}
                placeholder="e.g. Client always uses California law, net-30 payment terms"
                className="flex-1 text-xs px-3 py-2 rounded-lg outline-none"
                style={{ background: "var(--bg-tertiary)", border: "1px solid var(--border)", color: "var(--text-primary)" }}
                onFocus={(e) => { (e.currentTarget as HTMLElement).style.borderColor = "var(--accent-border)"; }}
                onBlur={(e) => { (e.currentTarget as HTMLElement).style.borderColor = "var(--border)"; }}
              />
              <button
                onClick={handleAddMemory}
                disabled={!newMemory.trim()}
                className="px-3 py-2 text-xs font-medium rounded-lg transition-colors disabled:opacity-40"
                style={{ background: "var(--accent)", color: "white" }}
              >
                Add
              </button>
            </div>

            {project.memory.length === 0 ? (
              <p className="text-xs text-center py-8" style={{ color: "var(--text-tertiary)" }}>
                No memory items yet. Add facts Claude should always know.
              </p>
            ) : (
              <div className="space-y-2">
                {[...project.memory].reverse().map((item) => (
                  <MemoryRow
                    key={item.id}
                    content={item.content}
                    onEdit={(content) => updateMemory(project.id, item.id, content)}
                    onDelete={() => removeMemory(project.id, item.id)}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Create project modal ───────────────────────────────────────────────────────
function CreateModal({ onClose, onCreate }: { onClose: () => void; onCreate: (title: string) => void }) {
  const [title, setTitle] = useState("");
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: "rgba(0,0,0,0.6)" }} onClick={onClose}>
      <div
        className="w-80 rounded-2xl p-6 flex flex-col gap-4"
        style={{ background: "var(--bg-primary)", border: "1px solid var(--border)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-sm font-semibold" style={{ color: "var(--text-primary)" }}>New Project</h3>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && title.trim()) { onCreate(title.trim()); onClose(); } if (e.key === "Escape") onClose(); }}
          placeholder="e.g. Q4 Contract Review"
          className="w-full text-sm px-3 py-2 rounded-lg outline-none"
          style={{ background: "var(--bg-secondary)", border: "1px solid var(--border)", color: "var(--text-primary)" }}
          autoFocus
          onFocus={(e) => { (e.currentTarget as HTMLElement).style.borderColor = "var(--accent-border)"; }}
        />
        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="px-3 py-1.5 text-xs rounded-lg transition-colors" style={{ color: "var(--text-secondary)" }}>Cancel</button>
          <button
            onClick={() => { if (title.trim()) { onCreate(title.trim()); onClose(); } }}
            disabled={!title.trim()}
            className="px-4 py-1.5 text-xs font-medium rounded-lg disabled:opacity-40"
            style={{ background: "var(--accent)", color: "white" }}
          >
            Create
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main view ──────────────────────────────────────────────────────────────────
export function ProjectsView() {
  const { projects, createProject, deleteProject } = useProjectStore();
  const [selectedId, setSelectedId] = useState<string | null>(projects[0]?.id ?? null);
  const [showCreate, setShowCreate] = useState(false);

  const selected = projects.find((p) => p.id === selectedId) ?? null;

  const handleCreate = (title: string) => {
    const p = createProject(title);
    setSelectedId(p.id);
  };

  const handleDelete = (id: string) => {
    deleteProject(id);
    setSelectedId(projects.find((p) => p.id !== id)?.id ?? null);
  };

  return (
    <div className="flex h-full">
      {/* Left: project list */}
      <div className="w-56 flex flex-col flex-shrink-0" style={{ borderRight: "1px solid var(--border)", background: "var(--bg-secondary)" }}>
        <div className="p-3 flex-shrink-0" style={{ borderBottom: "1px solid var(--border)" }}>
          <button
            onClick={() => setShowCreate(true)}
            className="w-full flex items-center gap-2 px-3 py-2 text-xs rounded-lg transition-colors"
            style={{ color: "var(--text-secondary)" }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = "var(--bg-hover)"; (e.currentTarget as HTMLElement).style.color = "var(--text-primary)"; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = "transparent"; (e.currentTarget as HTMLElement).style.color = "var(--text-secondary)"; }}
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
              <line x1="6" y1="1" x2="6" y2="11" /><line x1="1" y1="6" x2="11" y2="6" />
            </svg>
            New project
          </button>
        </div>

        <div className="flex-1 overflow-y-auto py-1">
          {projects.map((p) => (
            <button
              key={p.id}
              onClick={() => setSelectedId(p.id)}
              className="w-full text-left px-3 py-2.5 transition-colors"
              style={{
                background: selectedId === p.id ? "var(--bg-tertiary)" : "transparent",
              }}
              onMouseEnter={(e) => { if (selectedId !== p.id) (e.currentTarget as HTMLElement).style.background = "var(--bg-hover)"; }}
              onMouseLeave={(e) => { if (selectedId !== p.id) (e.currentTarget as HTMLElement).style.background = "transparent"; }}
            >
              <div className="flex items-center gap-2.5">
                <ProjectDot color={p.color} size={8} />
                <span className="text-xs font-medium truncate" style={{ color: selectedId === p.id ? "var(--text-primary)" : "var(--text-secondary)" }}>
                  {p.title}
                </span>
              </div>
              {p.description && (
                <p className="text-[10px] mt-0.5 truncate pl-[20.5px]" style={{ color: "var(--text-tertiary)" }}>
                  {p.description}
                </p>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Right: detail */}
      <div className="flex-1 min-w-0">
        {projects.length === 0 ? (
          <EmptyProjects onCreate={() => setShowCreate(true)} />
        ) : selected ? (
          <ProjectDetail project={selected} onDelete={() => handleDelete(selected.id)} />
        ) : (
          <div className="flex items-center justify-center h-full text-xs" style={{ color: "var(--text-tertiary)" }}>
            Select a project
          </div>
        )}
      </div>

      {showCreate && <CreateModal onClose={() => setShowCreate(false)} onCreate={handleCreate} />}
    </div>
  );
}
